import type { ReactNode } from 'react'
import type { AttributeSchema, Product } from '@/services/products'

export interface ProductColumn {
  key: string
  label: string
  isCore: boolean
  visible: boolean
}

export interface ProductColumnConfig {
  key: string
  label: string
  defaultVisible: boolean
  fieldKey?: string
  editType?: 'text' | 'number' | 'url' | 'textarea'
  render?: (product: Product) => ReactNode
  className?: string
  headerClassName?: string
  sortable?: boolean
}

// Core column definitions - single source of truth
export const CORE_PRODUCT_COLUMNS: ProductColumnConfig[] = [
  {
    key: 'externalId',
    label: 'ID',
    fieldKey: 'external_id',
    editType: 'text',
    defaultVisible: true,
    sortable: true,
  },
  {
    key: 'title',
    label: 'Title',
    fieldKey: 'title',
    editType: 'text',
    defaultVisible: true,
    sortable: true,
  },
  {
    key: 'description',
    label: 'Description',
    fieldKey: 'description',
    editType: 'textarea',
    defaultVisible: false,
    sortable: true,
  },
  {
    key: 'price',
    label: 'Price',
    fieldKey: 'price',
    editType: 'number',
    defaultVisible: true,
    className: 'text-right',
    headerClassName: 'text-right',
    sortable: true,
  },
  {
    key: 'currencyCode',
    label: 'Currency',
    fieldKey: 'currency_code',
    editType: 'text',
    defaultVisible: false,
    sortable: true,
  },
  {
    key: 'sku',
    label: 'SKU',
    fieldKey: 'sku',
    editType: 'text',
    defaultVisible: false,
    sortable: true,
  },
  {
    key: 'brandName',
    label: 'Brand',
    fieldKey: 'brand_name',
    editType: 'text',
    defaultVisible: true,
    sortable: true,
  },
  {
    key: 'category',
    label: 'Category',
    // No fieldKey — category edits require a picker (category_id FK), not inline text.
    defaultVisible: true,
    sortable: true,
  },
  {
    key: 'redirectUrl',
    label: 'Redirect URL',
    fieldKey: 'redirect_url',
    editType: 'url',
    defaultVisible: true,
    sortable: false,
  },
]

/**
 * Check if a key represents a redirect URL field
 */
export const isRedirectUrlKey = (key: string): boolean =>
  key === 'redirect_url' || /^redirect_url_\d+$/.test(key)

/**
 * Get a human-readable label for a redirect URL key (e.g., "Redirect 1", "Redirect 2")
 */
export const getRedirectLabel = (key: string): string => {
  if (key === 'redirect_url') return 'Redirect 1'
  const match = key.match(/^redirect_url_(\d+)$/)
  return match ? `Redirect ${match[1]}` : key
}

export const DEFAULT_ATTRIBUTE_ORDER: string[] = [
  'image',
  'title',
  'price',
  'description',
  'sku',
  'category',
]

/**
 * Get all available columns for display in FilterBar
 */
export function getAvailableColumns(
  attributeSchemas: AttributeSchema[],
  columnVisibility: Record<string, boolean>
): ProductColumn[] {
  // Core columns
  const coreColumns: ProductColumn[] = CORE_PRODUCT_COLUMNS.map((config) => ({
    key: config.key,
    label: config.label,
    isCore: true,
    visible: columnVisibility[config.key] ?? config.defaultVisible,
  }))

  // Dynamic columns from schemas
  const dynamicColumns: ProductColumn[] = attributeSchemas.map((schema) => ({
    key: schema.key,
    label: schema.displayName,
    isCore: false,
    visible: columnVisibility[schema.key] ?? true,
  }))

  return [...coreColumns, ...dynamicColumns]
}

/**
 * Get visible columns only for table rendering
 */
export function getVisibleColumns(
  attributeSchemas: AttributeSchema[],
  columnVisibility: Record<string, boolean>
): ProductColumn[] {
  return getAvailableColumns(attributeSchemas, columnVisibility).filter(
    (col) => col.visible
  )
}

/**
 * Get default column visibility for onboarding/upload step
 * Shows more columns by default for better product review
 */
export function getOnboardingColumnVisibility(): Record<string, boolean> {
  return {
    externalId: true,
    title: true,
    description: false,
    price: true,
    currencyCode: true,
    sku: false,
    brandName: true,
    category: true,
    redirectUrl: true,
  }
}

/**
 * Get onboarding column visibility with additional attributes
 */
export function getOnboardingColumnVisibilityWithAttributes(
  attributeSchemas: AttributeSchema[]
): Record<string, boolean> {
  const baseVisibility = getOnboardingColumnVisibility()

  // Add all additional attributes as visible
  attributeSchemas.forEach((schema) => {
    baseVisibility[schema.key] = true
  })

  return baseVisibility
}

/**
 * Get default column visibility for main products page
 */
export function getMainPageColumnVisibility(): Record<string, boolean> {
  return CORE_PRODUCT_COLUMNS.reduce(
    (acc, column) => {
      acc[column.key] = column.defaultVisible
      return acc
    },
    {} as Record<string, boolean>
  )
}
